import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { TrophyIcon, CalendarIcon, ThumbsUpIcon, StarIcon, ChevronDownIcon, ChevronUpIcon, AwardIcon, UsersIcon, BookOpenIcon, HeadphonesIcon, PenToolIcon, SparklesIcon, CheckCircleIcon } from 'lucide-react';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { useDarkMode } from '../../context/DarkModeContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
type Nominee = {
  id: number;
  name: string;
  work?: string;
  author?: string;
  votes: number;
  bio?: string;
};
type AwardCategory = {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  nominees: Nominee[];
  color: string;
  bgColor: string;
};
const awardCategories: AwardCategory[] = [{
  id: 1,
  title: 'Book of the Year',
  description: 'The most exceptional indie book of 2023',
  icon: <BookOpenIcon className="h-5 w-5" data-id="element-17076" data-mp="36t6bbjxbwv8v" />,
  color: 'text-amber-600',
  bgColor: 'bg-amber-50',
  nominees: [{
    id: 1,
    name: 'The Silent Echo',
    author: 'Elena Richards',
    votes: 2341,
    bio: 'A haunting debut that redefined gothic romance for a new generation.'
  }, {
    id: 2,
    name: 'Beyond the Veil',
    author: 'Marcus Chen',
    votes: 2187,
    bio: 'Epic fantasy with breathtaking world-building and unforgettable characters.'
  }, {
    id: 3,
    name: 'Whispers in the Dark',
    author: 'Sarah Johnson',
    votes: 1956,
    bio: 'A psychological thriller that kept readers guessing until the final page.'
  }, {
    id: 4,
    name: 'The Memory Collector',
    author: 'Aiden Park',
    votes: 1432,
    bio: 'A stunning debut exploring grief, memory, and the ties that bind us.'
  }]
}, {
  id: 2,
  title: 'Best Narrator',
  description: 'Celebrating exceptional audiobook performances',
  icon: <HeadphonesIcon className="h-5 w-5" data-id="element-17077" data-mp="28fr19h2kuo48" />,
  color: 'text-indigo-600',
  bgColor: 'bg-indigo-50',
  nominees: [{
    id: 1,
    name: 'Sarah Johnson',
    work: 'The Silent Echo',
    votes: 1247,
    bio: 'Her nuanced portrayal of complex characters earned universal acclaim.'
  }, {
    id: 2,
    name: 'Michael Harris',
    work: 'Beyond the Veil',
    votes: 1103,
    bio: 'Brought an entire cast of characters to vivid life across 22 hours.'
  }, {
    id: 3,
    name: 'Elena Martinez',
    work: 'Whispers in the Dark',
    votes: 986,
    bio: 'A masterclass in tension-building through voice alone.'
  }, {
    id: 4,
    name: 'James Okafor',
    work: 'The Memory Collector',
    votes: 874,
    bio: 'His warm, measured delivery made this emotional story truly unforgettable.'
  }]
}, {
  id: 3,
  title: 'Debut Author of the Year',
  description: 'Recognizing outstanding first-time authors',
  icon: <StarIcon className="h-5 w-5" data-id="element-17078" data-mp="3neem69hs037u" />,
  color: 'text-pink-600',
  bgColor: 'bg-pink-50',
  nominees: [{
    id: 1,
    name: 'Aiden Park',
    work: 'The Memory Collector',
    votes: 876,
    bio: 'Burst onto the scene with a debut that felt like the work of a seasoned pro.'
  }, {
    id: 2,
    name: 'Zoe Chen',
    work: 'Fractured Realms',
    votes: 812,
    bio: 'Built an entire fantasy universe in her first novel — and left us wanting more.'
  }, {
    id: 3,
    name: 'Lucas Rodriguez',
    work: 'Beneath the Surface',
    votes: 754,
    bio: 'A raw, honest voice that resonated deeply with readers worldwide.'
  }, {
    id: 4,
    name: 'Priya Nair',
    work: 'The Lotus Throne',
    votes: 631,
    bio: 'Wove mythology and modernity into a debut that defied categorization.'
  }]
}, {
  id: 4,
  title: 'Best Cover Design',
  description: 'Honoring outstanding visual artistry in book covers',
  icon: <PenToolIcon className="h-5 w-5" data-id="element-17079" data-mp="2fxgcvi6sm6vu" />,
  color: 'text-purple-600',
  bgColor: 'bg-purple-50',
  nominees: [{
    id: 1,
    name: 'Luna Design Co.',
    work: 'The Silent Echo',
    votes: 1089,
    bio: "A minimalist masterpiece that perfectly captures the book's haunting atmosphere."
  }, {
    id: 2,
    name: 'Inkwell Studios',
    work: 'Fractured Realms',
    votes: 967,
    bio: 'Intricate hand-drawn illustration that rewards closer inspection.'
  }, {
    id: 3,
    name: 'Vivid Press',
    work: 'Beyond the Veil',
    votes: 843,
    bio: 'Bold typography and striking color palette that stands out on any shelf.'
  }, {
    id: 4,
    name: 'Ember Creative',
    work: 'Whispers in the Dark',
    votes: 721,
    bio: "Subtle, unsettling imagery that perfectly sets the thriller's tone."
  }]
}, {
  id: 5,
  title: 'Best Romance',
  description: 'The standout indie romance of the year',
  icon: <SparklesIcon className="h-5 w-5" data-id="element-17080" data-mp="1n41pmp8zuvfb" />,
  color: 'text-rose-600',
  bgColor: 'bg-rose-50',
  nominees: [{
    id: 1,
    name: 'Always You',
    author: 'Mia Calloway',
    votes: 1654,
    bio: 'A second-chance romance that had readers sobbing and swooning in equal measure.'
  }, {
    id: 2,
    name: 'The Long Way Home',
    author: 'Cassie Bright',
    votes: 1421,
    bio: "Slow-burn perfection with characters you'll carry with you long after the last page."
  }, {
    id: 3,
    name: 'Midnight in Paris',
    author: 'Sophie Laurent',
    votes: 1198,
    bio: 'A whirlwind romance set against the most beautiful city in the world.'
  }, {
    id: 4,
    name: 'Unwritten Rules',
    author: 'Jade Morrison',
    votes: 987,
    bio: 'Enemies-to-lovers done with wit, heart, and genuine emotional depth.'
  }]
}, {
  id: 6,
  title: 'Community Choice',
  description: 'Voted entirely by the LitLaunch community',
  icon: <UsersIcon className="h-5 w-5" data-id="element-17081" data-mp="3gv0cakovjee3" />,
  color: 'text-green-600',
  bgColor: 'bg-green-50',
  nominees: [{
    id: 1,
    name: 'The Indie Bookshelf Podcast',
    work: 'Best Community Resource',
    votes: 2876,
    bio: 'Weekly deep-dives into indie publishing that have become essential listening.'
  }, {
    id: 2,
    name: 'BookTok Collective',
    work: 'Best Promotional Campaign',
    votes: 2341,
    bio: 'Grassroots campaign that launched three debut authors onto bestseller lists.'
  }, {
    id: 3,
    name: 'Readers for Readers',
    work: 'Best ARC Program',
    votes: 1987,
    bio: 'Connecting debut authors with passionate early readers since 2021.'
  }, {
    id: 4,
    name: 'The Cover Reveal Club',
    work: 'Best Newsletter',
    votes: 1654,
    bio: 'First look at upcoming indie releases, delivered with genuine enthusiasm.'
  }]
}];
const pastWinners = [{
  year: 2022,
  category: 'Book of the Year',
  title: 'The Glass Kingdom',
  author: 'Nadia Osei'
}, {
  year: 2022,
  category: 'Best Narrator',
  title: 'Voices of the Deep',
  author: 'James Okafor'
}, {
  year: 2022,
  category: 'Debut Author',
  title: 'First Light',
  author: 'Cora Whitfield'
}, {
  year: 2021,
  category: 'Book of the Year',
  title: 'Ember & Ash',
  author: 'Rowan Blake'
}, {
  year: 2021,
  category: 'Best Narrator',
  title: 'The Hollow Hours',
  author: 'Diane Mercer'
}, {
  year: 2021,
  category: 'Debut Author',
  title: 'Salt & Storm',
  author: 'Yuki Tanaka'
}];
const AwardsRecognition = () => {
  const {
    darkMode
  } = useDarkMode();
  const {
    isReader
  } = useUserRole();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [expandedCategory, setExpandedCategory] = useState<number | null>(1);
  const [votedNominees, setVotedNominees] = useState<Set<string>>(new Set());
  const totalVotes = (nominees: Nominee[]) => nominees.reduce((sum, n) => sum + n.votes, 0);
  const handleVote = (categoryId: number, nomineeId: number) => {
    const key = `${categoryId}-${nomineeId}`;
    setVotedNominees(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };
  return <TroveLayout data-id="element-17082" data-mp="ny2os13j3wij">
      {/* Hero */}
      <div className={`relative rounded-2xl overflow-hidden mb-10 ${darkMode ? 'bg-gradient-to-br from-amber-900/40 to-gray-800' : 'bg-gradient-to-br from-amber-50 to-amber-100'} border ${darkMode ? 'border-amber-800/30' : 'border-amber-200'}`} data-id="element-17083" data-mp="nz4086ogil0s">
        <div className="px-6 py-10 sm:px-10 sm:py-12" data-id="element-17084" data-mp="2zlekb4s45wlj">
          <div className="flex items-start gap-4 mb-4" data-id="element-17085" data-mp="2zlxrbqg870ks">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`} data-id="element-17086" data-mp="34k94aa1thr78">
              <TrophyIcon className="h-8 w-8 text-amber-500" data-id="element-17087" data-mp="39c98wfsu18a5" />
            </div>
            <div data-id="element-17088" data-mp="3ebc0axfw3fzn">
              <p className={`text-sm font-semibold uppercase tracking-widest mb-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-17089" data-mp="3e9smddhphh11">
                Annual Indie Lit Awards
              </p>
              <h1 className={`text-3xl sm:text-4xl font-bold ${dk.textPrimary}`} data-id="element-17090" data-mp="3nc1xa4gvzcey">
                Awards & Recognition 2023
              </h1>
            </div>
          </div>
          <p className={`text-base max-w-2xl mb-6 ${dk.textSecondary}`} data-id="element-17091" data-mp="1szc2lkip1z6i">
            Celebrating excellence in independent publishing. Cast your votes
            across six categories — every voice counts. Voting closes November
            30th.
          </p>
          <div className="flex flex-wrap items-center gap-4" data-id="element-17092" data-mp="861wtd0tjwhg">
            <div className={`flex items-center gap-2 px-4 py-2 ${rt.badgeRadius} text-sm font-medium ${darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-200 text-amber-800'}`} data-id="element-17093" data-mp="d4bqfj6wap0y">
              <CalendarIcon className="h-4 w-4" data-id="element-17094" data-mp="m59ycckgfgot" />
              21 days left to vote
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 ${rt.badgeRadius} text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-700'}`} data-id="element-17095" data-mp="r3bhsylsli0t">
              <UsersIcon className="h-4 w-4" data-id="element-17096" data-mp="10txo227mcw6u" />
              24,891 votes cast
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 ${rt.badgeRadius} text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-700'}`} data-id="element-17097" data-mp="14vrkfyas7e17">
              <TrophyIcon className="h-4 w-4 text-amber-500" data-id="element-17098" data-mp="2ucwnyx9w70p4" />6 categories
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-4 mb-12" data-id="element-17099" data-mp="2pfen7u613rs7">
        <h2 className={`text-xl font-bold mb-6 ${dk.textPrimary}`} data-id="element-17100" data-mp="2lb6xbtnu45se">
          Vote in All Categories
        </h2>

        {awardCategories.map(category => {
        const isExpanded = expandedCategory === category.id;
        const total = totalVotes(category.nominees);
        const leader = [...category.nominees].sort((a, b) => b.votes - a.votes)[0];
        return <div key={category.id} className={`rounded-xl border overflow-hidden transition-all ${dk.card}`} data-id="element-17101" data-mp="349rm3f296z2v">
              {/* Category Header */}
              <button className={`w-full flex items-center justify-between p-5 text-left transition-colors ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`} onClick={() => setExpandedCategory(isExpanded ? null : category.id)} data-id="element-17102" data-mp="lpul6bfjl9u">
                <div className="flex items-center gap-3" data-id="element-17103" data-mp="jorobhjfrt83">
                  <div className={`p-2 rounded-lg ${category.bgColor} ${category.color}`} data-id="element-17104" data-mp="5yxz2y40r6xu">
                    {category.icon}
                  </div>
                  <div data-id="element-17105" data-mp="3s5w7fsvh86eo">
                    <h3 className={`font-semibold text-base ${dk.textPrimary}`} data-id="element-17106" data-mp="3s5cyh1nt0scs">
                      {category.title}
                    </h3>
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-17107" data-mp="10wiqkxz9lq2">
                      {category.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-17108" data-mp="3n7kydkd09zxi">
                  <div className="hidden sm:flex items-center gap-2" data-id="element-17109" data-mp="1he4hm5kgilfn">
                    <span className={`text-xs ${dk.textMuted}`} data-id="element-17110" data-mp="1hbqnq01ijf9o">Leading:</span>
                    <span className={`text-xs font-medium ${dk.textSecondary}`} data-id="element-17111" data-mp="1lewsf38z8ye1">
                      {leader.name}
                    </span>
                  </div>
                  {isExpanded ? <ChevronUpIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-17112" data-mp="31xgp34bzauh0" /> : <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-17113" data-mp="3fov6x7u5gpmh" />}
                </div>
              </button>

              {/* Nominees */}
              {isExpanded && <div className={`border-t ${dk.divider}`} data-id="element-17114" data-mp="3pjn5advk3gib">
                  <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-17115" data-mp="3kn6ibn03f86t">
                    {category.nominees.map((nominee, index) => {
                const pct = Math.round(nominee.votes / total * 100);
                const isLeading = index === 0;
                const voteKey = `${category.id}-${nominee.id}`;
                const hasVoted = votedNominees.has(voteKey);
                return <div key={nominee.id} className={`relative rounded-lg p-4 border transition-all ${isLeading ? darkMode ? 'border-amber-600/40 bg-amber-900/20' : 'border-amber-200 bg-amber-50/60' : `${dk.surfaceMuted} ${dk.borderSubtle}`}`} data-id="element-17116" data-mp="1zav8k1ayd81c" data-mpi={index}>
                          {isLeading && <div className="absolute top-3 right-3" data-id="element-17117" data-mp="2fmf7dj23ihzq">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`} data-id="element-17118" data-mp="1i0872lctrbt6">
                                Leading
                              </span>
                            </div>}

                          <div className="mb-3 pr-16" data-id="element-17119" data-mp="1wqfd21lnb2cz">
                            <p className={`font-semibold text-sm ${dk.textPrimary}`} data-id="element-17120" data-mp="z8fegs1o3slz">
                              {nominee.name}
                            </p>
                            <p className={`text-xs ${dk.textMuted}`} data-id="element-17121" data-mp="wo0u4ihq2eph">
                              {'author' in nominee && nominee.author ? `by ${nominee.author}` : nominee.work}
                            </p>
                            {nominee.bio && <p className={`text-xs mt-1.5 leading-relaxed ${dk.textSecondary}`} data-id="element-17122" data-mp="dpy35y5m67p1">
                                {nominee.bio}
                              </p>}
                          </div>

                          {/* Vote bar */}
                          <div className="mb-3" data-id="element-17123" data-mp="3v7r0b471sl8i">
                            <div className="flex justify-between items-center mb-1" data-id="element-17124" data-mp="3q7boih2ezlsn">
                              <span className={`text-xs ${dk.textMuted}`} data-id="element-17125" data-mp="3q9ftl2c1uqui">
                                {nominee.votes.toLocaleString()} votes
                              </span>
                              <span className={`text-xs font-medium ${dk.textSecondary}`} data-id="element-17126" data-mp="3bjbaplh3cigw">
                                {pct}%
                              </span>
                            </div>
                            <div className={`h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-17127" data-mp="37i9d192toeh1">
                              <div className={`h-full rounded-full transition-all duration-500 ${isLeading ? 'bg-amber-500' : darkMode ? 'bg-gray-500' : 'bg-gray-400'}`} style={{
                        width: `${pct}%`
                      }} data-id="element-17128" data-mp="35cfim1y114g" />
                            </div>
                          </div>

                          <button onClick={() => handleVote(category.id, nominee.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${hasVoted ? darkMode ? 'bg-green-800/40 text-green-400 border border-green-700/50' : 'bg-green-50 text-green-700 border border-green-200' : rt.secondaryButton}`} data-id="element-17129" data-mp="mtlm4148h9nh">
                            {hasVoted ? <>
                                <CheckCircleIcon className="h-3.5 w-3.5" data-id="element-17130" data-mp="d99nkrjhla7y" />{' '}
                                Voted
                              </> : <>
                                <ThumbsUpIcon className="h-3.5 w-3.5" data-id="element-17131" data-mp="n4um2fpcjscy" />{' '}
                                {isReader ? 'Vote' : 'Nominate'}
                              </>}
                          </button>
                        </div>;
              })}
                  </div>
                </div>}
            </div>;
      })}
      </div>

      {/* Submit Nomination CTA */}
      {!isReader && <div className={`rounded-xl p-6 mb-12 border ${darkMode ? 'bg-purple-900/20 border-purple-700/30' : 'bg-purple-50 border-purple-200'}`} data-id="element-17132" data-mp="3s5cqd6gxs5vd">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-17133" data-mp="5piajglznv0y">
            <div data-id="element-17134" data-mp="ap4mzntzt78p">
              <h3 className={`font-bold text-base mb-1 ${dk.textPrimary}`} data-id="element-17135" data-mp="anjtm1jezucf">
                Submit a Nomination
              </h3>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-17136" data-mp="fpiun4romf67">
                Know an indie author, narrator, or designer who deserves
                recognition? Submit a nomination before November 15th.
              </p>
            </div>
            <button className={`shrink-0 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors ${rt.bgDark}`} data-id="element-17137" data-mp="2lgwhhofyjge2">
              Submit Nomination
            </button>
          </div>
        </div>}

      {/* Past Winners */}
      <section data-id="element-17138" data-mp="2e589gju0x98b">
        <h2 className={`text-xl font-bold mb-5 ${dk.textPrimary}`} data-id="element-17139" data-mp="2e4q50zx7jopb">
          Past Winners
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-17140" data-mp="295lxedpxlz13">
          {pastWinners.map((winner, i) => <div key={i} className={`flex items-start gap-3 p-4 rounded-lg border ${dk.cardSubtle}`} data-id="element-17141" data-mp="248dbc21nxauk" data-mpi={i}>
              <div className={`p-1.5 rounded-md shrink-0 ${darkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`} data-id="element-17142" data-mp="31sgbmsw5o264">
                <TrophyIcon className="h-4 w-4 text-amber-500" data-id="element-17143" data-mp="2rvlhem6x0nxn" />
              </div>
              <div data-id="element-17144" data-mp="2nvktmaxxf0dx">
                <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-17145" data-mp="2ntzzgmfixjgl">
                  {winner.year} · {winner.category}
                </p>
                <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-17146" data-mp="1ci8wsk0l3i19">
                  {winner.title}
                </p>
                <p className={`text-xs ${dk.textMuted}`} data-id="element-17147" data-mp="80xslyuk0srz">by {winner.author}</p>
              </div>
            </div>)}
        </div>
      </section>
    </TroveLayout>;
};
export default AwardsRecognition;